/**
 * MilestoneForm Component
 *
 * Modal form for creating or editing milestones.
 * Includes deliverables list management.
 *
 * @module components/milestones/MilestoneForm
 */

import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Button,
  Row,
  Col,
  InputGroup,
  ListGroup,
  Spinner,
} from 'react-bootstrap';

const MilestoneForm = ({ show, onHide, onSave, milestone = null, loading = false }) => {
  const isEditing = !!milestone;

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    weight_percentage: 0,
    target_date: '',
    acceptance_criteria: '',
    billable: true,
    billing_amount: '',
    order_index: 0,
  });
  const [deliverables, setDeliverables] = useState([]);
  const [newDeliverable, setNewDeliverable] = useState('');

  useEffect(() => {
    if (milestone) {
      setFormData({
        title: milestone.title || '',
        description: milestone.description || '',
        weight_percentage: milestone.weight_percentage || 0,
        target_date: milestone.target_date || '',
        acceptance_criteria: milestone.acceptance_criteria || '',
        billable: milestone.billable !== false,
        billing_amount: milestone.billing_amount || '',
        order_index: milestone.order_index || 0,
      });
      setDeliverables(
        Array.isArray(milestone.deliverables) ? milestone.deliverables : []
      );
    } else {
      setFormData({
        title: '',
        description: '',
        weight_percentage: 0,
        target_date: '',
        acceptance_criteria: '',
        billable: true,
        billing_amount: '',
        order_index: 0,
      });
      setDeliverables([]);
    }
  }, [milestone, show]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddDeliverable = () => {
    if (newDeliverable.trim()) {
      setDeliverables((prev) => [
        ...prev,
        { title: newDeliverable.trim(), completed: false },
      ]);
      setNewDeliverable('');
    }
  };

  const handleRemoveDeliverable = (index) => {
    setDeliverables((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      weight_percentage: parseFloat(formData.weight_percentage) || 0,
      billing_amount: formData.billing_amount ? parseFloat(formData.billing_amount) : null,
      order_index: parseInt(formData.order_index) || 0,
      deliverables,
    });
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditing ? 'Editar Hito' : 'Nuevo Hito'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="mb-3">
            <Col md={8}>
              <Form.Group>
                <Form.Label>Titulo *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  required
                  placeholder="Ej: Instalacion completada"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Peso (%)</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="number"
                    min="0"
                    max="100"
                    step="0.5"
                    value={formData.weight_percentage}
                    onChange={(e) => handleChange('weight_percentage', e.target.value)}
                  />
                  <InputGroup.Text>%</InputGroup.Text>
                </InputGroup>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Descripción</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Descripción del hito..."
            />
          </Form.Group>

          <Row className="mb-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Fecha Objetivo</Form.Label>
                <Form.Control
                  type="date"
                  value={formData.target_date}
                  onChange={(e) => handleChange('target_date', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Orden</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  value={formData.order_index}
                  onChange={(e) => handleChange('order_index', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Facturable</Form.Label>
                <div className="d-flex align-items-center gap-3 mt-1">
                  <Form.Check
                    type="switch"
                    checked={formData.billable}
                    onChange={(e) => handleChange('billable', e.target.checked)}
                    label={formData.billable ? 'Si' : 'No'}
                  />
                </div>
              </Form.Group>
            </Col>
          </Row>

          {formData.billable && (
            <Form.Group className="mb-3">
              {/* #617: sin "(USD)". `milestones` no tiene columna de moneda. */}
              <Form.Label>Monto de Facturacion</Form.Label>
              <Form.Control
                type="number"
                min="0"
                step="0.01"
                value={formData.billing_amount}
                onChange={(e) => handleChange('billing_amount', e.target.value)}
                placeholder="Dejar vacio para calcular por peso"
              />
              <Form.Text className="text-muted">
                Si se deja vacio, se calcula como peso% del total de la cotizacion.
              </Form.Text>
            </Form.Group>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Criterios de Aceptacion</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={formData.acceptance_criteria}
              onChange={(e) => handleChange('acceptance_criteria', e.target.value)}
              placeholder="Criterios para considerar el hito completado..."
            />
          </Form.Group>

          {/* Deliverables */}
          <Form.Group>
            <Form.Label>Entregables</Form.Label>
            <InputGroup className="mb-2">
              <Form.Control
                type="text"
                value={newDeliverable}
                onChange={(e) => setNewDeliverable(e.target.value)}
                placeholder="Agregar entregable..."
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddDeliverable();
                  }
                }}
              />
              <Button
                variant="outline-primary"
                onClick={handleAddDeliverable}
                disabled={!newDeliverable.trim()}
              >
                <i className="bi bi-plus-lg"></i>
              </Button>
            </InputGroup>
            {deliverables.length > 0 && (
              <ListGroup variant="flush">
                {deliverables.map((d, idx) => (
                  <ListGroup.Item
                    key={idx}
                    className="d-flex justify-content-between align-items-center py-2 px-0"
                  >
                    <span>
                      <i className={`bi ${d.completed ? 'bi-check-circle-fill text-success' : 'bi-circle'} me-2`}></i>
                      {d.title}
                    </span>
                    <Button
                      variant="link"
                      size="sm"
                      className="text-danger p-0"
                      onClick={() => handleRemoveDeliverable(idx)}
                    >
                      <i className="bi bi-x-lg"></i>
                    </Button>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={loading}>
            Cancelar
          </Button>
          <Button type="submit" variant="primary" disabled={loading || !formData.title.trim()}>
            {loading ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-1" />
                Guardando...
              </>
            ) : (
              isEditing ? 'Actualizar' : 'Crear Hito'
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default MilestoneForm;
